import React, { useEffect, useState } from "react"
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, IconButton, Skeleton, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { RmDashboardType, filterType } from "../../../redux-store/types/api-types";
import CircleIcon from '@mui/icons-material/Circle';
import CustomDropdown from "../../../lib/formik/CustomDropdown";
import { calculateOthersInPieChart, formatCurrency, toINR } from "../../../utils/utilityFunctions";
import { table } from "console";
import { BlueRightIcon } from "../../../assets/SvgIcons";
import ListModel from "./ListModel";
import { RootStateType } from "../../../redux-store/reducers";
import { useSelector } from "react-redux";

interface Props {
    labelData: filterType[];
    data: Array<Array<string | number>>;
    totalValue: filterType;
    isLoading: boolean;
    showSlider?: boolean
    align?: 'center' | 'end' | 'start' | 'left' | 'right',
    defaultValue?: filterType,
    title?: string;
    maskedData?: boolean;
    colorArr?: string[];
    labelOrderEnabled?: boolean;
}

interface defaultTotal {
    title: string,
    value: number
}

const pieChartColor = ["#00A76F", "#FFAB00", "#3C92DC ", "#B8E8FC", "#FF563080", "#0D3C6B"]

export default function CustomePieChart(props: Props): JSX.Element {
    const theme = useTheme();
    const xllaptopView = useMediaQuery(theme.breakpoints.down("xl"))
    const laptopView = useMediaQuery(theme.breakpoints.down("lg"))
    const tableView = useMediaQuery(theme.breakpoints.down("md"))
    const mobileView = useMediaQuery(theme.breakpoints.down("sm"))

    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const modifiedData = calculateOthersInPieChart({ labelData: props.labelData, totalValue: props.totalValue, data: props.data, labelOrderEnabled: props?.labelOrderEnabled })

    const avoidedLabels = ['Issuer', 'Issuers', 'Sectors', 'Sector', 'Instruments', 'Instrument', 'Company', 'Companies', 'Scheme', 'Schemes']

    const [subtit, setSubTit] = useState("all");
    const [open, setOpen] = useState(false);
    const [centerTitle, setCenterTitle] = useState<defaultTotal>({
        title: props?.totalValue?.label || "",
        value: Number(props?.totalValue?.value) || 0
    })
    const [options, setOptions] = useState<Highcharts.Options>({
        title: {
            text: "",
            align: "center",
        },
        accessibility: {
            description: props?.title || 'Pie chart showing asset distribution',
        },
        credits: {
            enabled: false,
        },
        subtitle: {
            useHTML: true,
            text: `
            <div style="text-align: center; padding: 22px;">
            <span style="font-size: ${mobileView ? 12 : tableView ? 14 : 16}px; color: #111927; font-weight: 500; opacity: 0.5">${centerTitle?.title || 'Loading...'}</span> 
            <br> 
            <span style="font-size: ${mobileView ? 18 : tableView ? 20 : 22}px; color: #111927; font-weight: 500;">${centerTitle?.value > 0 ? avoidedLabels.includes(centerTitle?.title) ? centerTitle?.value : formatCurrency({ value: centerTitle?.value, locale, currencyCode }) : '0'}</span>
            <br> 
            <span style="font-size: ${mobileView ? 10 : tableView ? 12 : 14}px; color: #111927; font-weight: 500;">${centerTitle?.value > 0 ? avoidedLabels.includes(centerTitle?.title) ? '' : '(Approx.)' : '0'}</span>
            </div>`,
            verticalAlign: "middle",
            y: 30,
            style: {
                fontSize: "24px",
            }
        },
        plotOptions: {
            series: {
                // borderWidth: 0,
                // colorByPoint: true,
                // type: "pei",
                dataLabels: {
                    enabled: false,
                    style: {
                    }
                    // crop: false,
                    // distance: "-20%",
                },

            },


        },
        tooltip: {
            enabled: true,
            formatter: function (this: any) {
                // console.log("asdf", this.point.name);
                setSubTit(this.point.name);
                // console.log(this.point);
                setCenterTitle((pre: any) => {
                    if (pre?.title === this.point.name) {
                        return pre
                    }
                    else {
                        return { title: this.point.name, value: this.point.y }
                    }
                });
                return "";
            },

            style: {
                display: "none",
            },

        },
        // colors: pieChartColor,
        colors: props?.colorArr || ["#00A76F", "#FFAB00", "#3C92DC ", "#B8E8FC", "#FF563080", "#0D3C6B"],
        series: [
            {
                type: "pie",
                name: "Energy production",
                innerSize: mobileView ? "70%" : "90%",
                size: mobileView ? "100%" : "83%",
                animation: false,
                // data: [
                //     ["Total", 20.0231569],
                //     ["UK", 15.135],
                //     ["US", 3.8491569],
                //     ["Germany", 3.8491569],
                //     ["France", 3.8491569],
                // ],
                data: modifiedData?.data?.length < 5 ? modifiedData?.data : modifiedData?.data?.slice(0, 5),
            },
        ],
        chart: {
            style: {
                fontFamily: theme.typography.fontFamily,
            },
            backgroundColor: "transparent",
            polar: true,
            type: 'line',
            height: mobileView ? 260 : tableView ? 280 : laptopView ? 300 : 320, // taller for mobile
        }
    })

    useEffect(() => {
        setCenterTitle(props.defaultValue as defaultTotal);
    }, [props.defaultValue])

    useEffect(() => {
        setOptions((pre: any) => {
            return {
                ...pre,
                subtitle: {
                    ...pre.subtitle,
                    text: `
                    <div style="text-align: center; padding: 22px;">
                    <span style="font-size: ${mobileView ? 12 : tableView ? 14 : 16}px; color: #111927; font-weight: 500; opacity: 0.5">${centerTitle?.title || 'Loading...'}</span> 
                    <br> 
                    <span style="font-size: ${mobileView ? 18 : tableView ? 20 : 22}px; color: #111927; font-weight: 500;">${props?.maskedData ? "***" : (centerTitle?.value > 0 ? avoidedLabels.includes(centerTitle?.title) ? centerTitle?.value : formatCurrency({ value: centerTitle?.value, locale, currencyCode }) : '0')}</span>
                    <br> 
                    <span style="font-size: ${mobileView ? 10 : tableView ? 12 : 14}px; color: #111927; font-weight: 500;">${avoidedLabels.includes(centerTitle?.title) ? '' : '(Approx.)'}</span>
                    </div>`,
                },
                series: [
                    {
                        type: "pie",
                        name: "Energy production",
                        innerSize: "90%",
                        size: "83%",
                        animation: false,
                        // data: props?.data?.length < 6 ? [...props.data] : [...calculateOthersInPieChart({ labelData: props.labelData, totalValue: props.totalValue, data: props.data })?.data?.slice(0, 5)],
                        data: modifiedData?.data?.length < 5 ? [...modifiedData?.data] : modifiedData?.data?.slice(0, 5),
                    },
                ],
            }
        })
    }, [props.data, centerTitle])

    useEffect(() => {
        const handle = setInterval(() => {

            setCenterTitle({ title: props?.totalValue?.label || '', value: Number(props?.totalValue?.value) || 0 });
        }, 1000);

        return () => {
            clearInterval(handle);
        };
    }, [subtit, props?.totalValue?.label, props?.totalValue?.value]);
    return (

        <>

            <Box component={'div'} sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: props.align ? props.align : { xs: 'center', lg: 'space-between' },

                alignItems: 'center',
                width: '100%',
                flexWrap: { xs: 'wrap', sm: 'wrap', md: 'wrap', lg: 'nowrap', xl: 'nowrap', },
            }}>
                {!props.isLoading && props?.data?.length > 0 ?
                    <HighchartsReact
                        showLoading={true}
                        highcharts={Highcharts}
                        options={options}
                        allowChartUpdate={true}
                        containerProps={{
                            style: {
                                height: mobileView ? 260 : tableView ? 250 : laptopView ? 280 : 300,
                                width: '100%',
                                maxWidth: mobileView ? '100%' : tableView ? '100%' : laptopView ? '100%' : '270px',
                                minWidth: mobileView ? '200px' : undefined,
                            }
                        }}
                        background="transparent" />
                    :
                    <ChartSkeletonLoader />
                }


                {props.showSlider &&
                    <Box component={'div'} sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: { lg: '20px', md: '15px', sm: '10px', xs: '10px' },
                        width: '100%',
                        maxWidth: '300px',
                    }}>
                        {
                            !props.isLoading ?
                                props?.labelData?.length < 6 ?
                                    props.labelData?.map((data, index) => (
                                        <Box key={index} style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            gap: 15
                                        }}>
                                            <CircleIcon sx={{ color: pieChartColor[index], width: '11px', height: '11px', marginTop: { lg: '8px', sm: '4px', xs: '4px' } }} />
                                            <Box component={'div'} style={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'space-between', gap: '15px' }}>

                                                <Typography style={{ textWrap: 'wrap', color: 'text.primary', maxWidth: '140px' }} variant="subtitle_400" >{data.label}</Typography>
                                                <Typography style={{ textWrap: 'nowrap', color: 'text.primary' }} variant="subtitle_400" align="right">{(props.maskedData) ? '***' : (Number(data?.value))?.toFixed(2) + '%'}</Typography>
                                            </Box>
                                        </Box>
                                    )) : props?.labelData?.slice(0, 6)?.map((data, index) => (
                                        <Box key={index} style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            gap: 15
                                        }}>

                                            <CircleIcon sx={{
                                                color: 5 === index ? "transparent" : pieChartColor[index],
                                                width: '11px', height: '11px', marginTop: { lg: '8px', sm: '4px', xs: '4px' }
                                            }} />
                                            <Box component={'div'} style={{
                                                display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'space-between', gap: '15px'

                                            }}
                                                sx={{
                                                    alignItems: { xs: 'center' }
                                                }}>

                                                <Typography style={{ textWrap: 'wrap', color: 'text.primary', maxWidth: '140px' }} variant="subtitle_400" >{5 === index ? 'Show more' : data.label}</Typography>
                                                {5 === index ?
                                                    <IconButton aria-label="Show more details" onClick={() => setOpen(true)}>
                                                        <BlueRightIcon />
                                                    </IconButton> :

                                                    <Typography style={{ textWrap: 'nowrap', color: 'text.primary' }} variant="subtitle_400" align="right">{(props.maskedData) ? '***' : (Number(data?.value))?.toFixed(2) + '%'}</Typography>
                                                }
                                            </Box>
                                        </Box>
                                    ))

                                :

                                <ChartListSkeletonLoader />}

                    </Box>
                }

            </Box>
            <ListModel open={open} handleClose={() => setOpen(false)} labelData={props?.labelData} title={props.title} maskedData={props?.maskedData} />
        </>
    )
}


export const ChartSkeletonLoader = () => {
    return (
        <>
            <Skeleton variant="circular" height={200} style={{
                margin: 35,
                width: '100%',
                maxWidth: '200px',
            }} />
        </>
    )
}

export const ChartListSkeletonLoader = () => {
    return (
        <>
            {/* {Array(5).map((val, i) => ( */}
            <Skeleton animation={'wave'} width={200} />
            <Skeleton animation={'wave'} width={200} />
            <Skeleton animation={'wave'} width={200} />
            <Skeleton animation={'wave'} width={200} />
            <Skeleton animation={'wave'} width={200} />
            {/* ))} */}
        </>
    )
}


export const FilterSkeleton = () => {
    return (
        // <>
        <Skeleton animation={'wave'} width={200} height={30} />
        // </>
    )
}
